import { Map } from 'immutable';

import { shapeTaxonomyAttributesForAPI } from '../product/shapeTaxonomyAttributes';
import { getAttributesForCategory } from '../product/taxonomy';
import { getMaterialsError } from '../validations/materials';
import { newShipping } from '../new/product';
import { getSize } from '../iterable/getSize';
import { keepOld } from '../iterable/keepOld';
import {
  setCategoryErrors,
  setFilesErrors,
  setIsSupplyErrors,
  setMaterialsErrors,
  setProductionPartnersErrors,
  setShippingErrors,
  setTaxonomyAttributesErrors,
  setVariationsErrors,
  setWhenMadeErrors,
  setWhoMadeErrors,
} from '../product/errors';

import { AMOUNT, OPERATIONS, VALUE } from '../../constants/product';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { EMPTY, NONE } from '../../constants';
import { MODALS } from '../../constants/modal';
import { FORM } from '../../constants/forms';
import ATTRIBUTE from '../../constants/attributes';

export function editDetails({ channel, product: source, ...values }) {
  let product = source;
  const operations = [];
  let confirmation;

  switch (channel) {
    case ETSY: {
      if (values[ATTRIBUTE.CATEGORY] !== undefined) {
        const type = OPERATIONS[channel].CATEGORY;
        const { category = AMOUNT.ZERO, index } = values[ATTRIBUTE.CATEGORY];
        const taxonomy = String(index);
        operations.push(Map({ taxonomy, type, value: category }));
        product = setCategoryErrors({
          channel,
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], category),
        });

        const { changed, taxonomyAttributes } = getAttributesForCategory({
          category,
          taxonomyAttributes: product.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES], EMPTY.MAP),
        });

        product = setTaxonomyAttributesErrors({
          channel,
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES], taxonomyAttributes),
        });

        if (changed) {
          confirmation = MODALS.CONFIRMATIONS.CHANGE_CATEGORY;
        }
      }

      if (values[ATTRIBUTE.SECTION] !== undefined) {
        const value = values[ATTRIBUTE.SECTION];
        operations.push(Map({ type: OPERATIONS[channel].SECTION, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE], value === 'none' ? null : value);
      }

      if (values[ATTRIBUTE.SHOULD_AUTO_RENEW] !== undefined) {
        const value = values[ATTRIBUTE.SHOULD_AUTO_RENEW];
        operations.push(Map({ type: OPERATIONS[channel].SHOULD_AUTO_RENEW, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.SHOULD_AUTO_RENEW, VALUE], value);
      }

      if (values[ATTRIBUTE.IS_SUPPLY] !== undefined) {
        const value = values[ATTRIBUTE.IS_SUPPLY];
        operations.push(Map({ type: OPERATIONS[channel].IS_SUPPLY, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.IS_SUPPLY, VALUE], value);
        product = setIsSupplyErrors({ channel, product });
        product = setProductionPartnersErrors({ channel, product });
      }

      if (values[ATTRIBUTE.WHEN_MADE] !== undefined) {
        const value = values[ATTRIBUTE.WHEN_MADE];
        operations.push(Map({ type: OPERATIONS[channel].WHEN_MADE, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, VALUE], value);
        product = setWhenMadeErrors({ channel, product });
        product = setProductionPartnersErrors({ channel, product });
        product = setFilesErrors({ channel, product });
      }

      if (values[ATTRIBUTE.WHO_MADE] !== undefined) {
        const value = values[ATTRIBUTE.WHO_MADE];
        operations.push(Map({ type: OPERATIONS[channel].WHO_MADE, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.WHO_MADE, VALUE], value);
        product = setWhoMadeErrors({ channel, product });
        product = setProductionPartnersErrors({ channel, product });
      }

      if (values[ATTRIBUTE.PRODUCTION_PARTNERS] !== undefined) {
        const value = values[ATTRIBUTE.PRODUCTION_PARTNERS];
        operations.push(Map({ type: OPERATIONS[channel].PRODUCTION_PARTNERS, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE], value);
        product = setProductionPartnersErrors({ channel, product });
      }

      if (values[ATTRIBUTE.DIGITAL] !== undefined) {
        const value = values[ATTRIBUTE.DIGITAL];
        operations.push(Map({ type: OPERATIONS[channel].DIGITAL, value }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE], value);

        if (value) {
          if (!product.hasIn([FORM.FILES, VALUE])) {
            product = product.setIn([FORM.FILES, VALUE], EMPTY.LIST);
          }
        } else {
          if (!product.has(FORM.SHIPPING)) {
            product = product.set(FORM.SHIPPING, newShipping({ channel }));
          }
        }

        product = setFilesErrors({ channel, product });
        product = setShippingErrors({ channel, product });
        product = setWhenMadeErrors({ channel, product });
        product = setVariationsErrors({ channel, product });
      }

      if (values[ATTRIBUTE.MATERIALS] !== undefined) {
        const { [ATTRIBUTE.MATERIALS]: { materials, newMaterials }} = values;

        product = product.setIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, VALUE], materials);

        if (getSize(newMaterials)) {
          product = product.setIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, ATTRIBUTE.NEW_MATERIALS], newMaterials);
        } else if (getSize(product.setIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, ATTRIBUTE.NEW_MATERIALS]))) {
          product = product.deleteIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, ATTRIBUTE.NEW_MATERIALS], newMaterials);
        }

        const allMaterials = product
          .getIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, VALUE])
          .mergeWith(
            keepOld,
            product.getIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, ATTRIBUTE.NEW_MATERIALS], EMPTY.ORDERED_MAP)
          );

        if (!getMaterialsError({ channel, materials: allMaterials })) {
          product = setMaterialsErrors({ channel, product });
          operations.push(Map({ type: OPERATIONS[channel].MATERIALS.CHANGE_TO, value: allMaterials.toList() }));
        }
      }

      if (values[ATTRIBUTE.TAXONOMY_ATTRIBUTES] !== undefined) {
        for (const key in values[ATTRIBUTE.TAXONOMY_ATTRIBUTES]) {
          if (values[ATTRIBUTE.TAXONOMY_ATTRIBUTES].hasOwnProperty(key)) {
            const propertyId = parseInt(key);
            const attribute = {
              scaleId: values[ATTRIBUTE.TAXONOMY_ATTRIBUTES][key].get('scaleId', null),
              values: values[ATTRIBUTE.TAXONOMY_ATTRIBUTES][key].get('values', EMPTY.LIST),
              valueIds: values[ATTRIBUTE.TAXONOMY_ATTRIBUTES][key].get('valueIds', EMPTY.LIST),
            };

            product = !attribute.scaleId && getSize(attribute.values) === 0 && getSize(attribute.valueIds) === 0
              ? product.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES, propertyId], EMPTY.MAP)
              : product.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES, propertyId], Map(attribute));

            product = setTaxonomyAttributesErrors({ channel, product });
          }
        }

        const value = shapeTaxonomyAttributesForAPI({
          taxonomyAttributes: product.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES]),
        });

        if (getSize(value) > 0) {
          operations.push(Map({ type: OPERATIONS[channel].TAXONOMY_ATTRIBUTES, value }));
        }
      }

      break;
    }

    case SHOPIFY: {
      if (values[ATTRIBUTE.CATEGORY] !== undefined) {
        const type = OPERATIONS[channel].CATEGORY;
        const { category = null } = values[ATTRIBUTE.CATEGORY];
        operations.push(Map({ type, value: category }));
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], category);
      }

      if (values[ATTRIBUTE.COLLECTIONS] !== undefined) {
        const collections = values[ATTRIBUTE.COLLECTIONS];
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.COLLECTIONS, VALUE], collections);
        operations.push(
          Map({
            type: OPERATIONS[channel].COLLECTIONS,
            value: getSize(collections) ? collections : collections.push(''),
          })
        );
      }

      if (values[ATTRIBUTE.PRODUCT_TYPE] !== undefined) {
        const value = values[ATTRIBUTE.PRODUCT_TYPE];
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_TYPE, VALUE], value === NONE ? null : value);
        operations.push(Map({ type: OPERATIONS[channel].PRODUCT_TYPE, value: value === NONE ? '' : value }));
      }

      if (values[ATTRIBUTE.VENDOR] !== undefined) {
        const value = values[ATTRIBUTE.VENDOR];
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.VENDOR, VALUE], value === NONE ? null : value);
        operations.push(Map({ type: OPERATIONS[channel].VENDOR, value: value === NONE ? '' : value }));
      }

      break;
    }

    default: {
      break;
    }
  }

  return { confirmation, product, operations };
}
